import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getQueryDefinitionAction } from "../actions";
import { getRows, getLoading, getDateFormat } from "../selectors";
import { CLEAR_STATE } from "../actionTypes";
import { getIsExportingGrid } from "@icarius-app/selectors";
import { getLocalizedString } from "@icarius-localization/strings";
import CommonPage from "@icarius-common/commonPage";
import paths from "@icarius-router/paths";
import FormContainer from "./form/formContainer";
import ConfirmationDialog from "./dialogs/confirmationDialog";
import DuplicateDialog from "./dialogs/duplicateDialog";
import CreateOrUpdateAiAssistedDialog from "./dialogs/createOrUpdateAiAssistedDialog";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import useABM from "./useABM";
import MenuItemWithIcon from "@icarius-common/MenuItemWithIcon";
import SearchConcept from "./searchConcept";

const QueryDefinition = ({ handleOpenParameterPage, handleGoBack }) => {
    const dispatch = useDispatch();

    const data = useSelector(getRows);
    const dateFormat = useSelector(getDateFormat);
    const isLoading = useSelector(getLoading);
    const isExportinGrid = useSelector(getIsExportingGrid);

    useEffect(() => {
        dispatch(getQueryDefinitionAction());
        return () => {
            dispatch({ type: CLEAR_STATE });
        };
    }, [dispatch]);

    const {
        deleteDialogIsOpen,
        createEditDialogIsOpen,
        duplicateDialogIsOpen,
        selectedItem,
        handleOpenCreateDialog,
        handleOpenEditDialog,
        handleCloseEditDialog,
        handleOpenDeleteDialog,
        handleCloseDeleteDialog,
        handleOpenDuplicateDialog,
        handleCloseDuplicateDialog,
        handleDelete,
        handleDuplicate,
        handleExecute,
        handleExport,
        handleImport,
        hasAiQueryGenerationAccess,
        handleOpenCreateAIQueryDialog,
        aiAssistedCreateDialogIsOpen,
        handleCloseAiAssistedCreateDialog,
    } = useABM();

    const goBackItem = () => (
        <CustomIconButton title={getLocalizedString("goBack")} onClick={handleGoBack} type={"goBack"} />
    );

    const addItem = () => (
        <CustomIconButton title={getLocalizedString("create")} onClick={() => handleOpenCreateDialog()} type={"add"} />
    );

    const addWithAiItem = (gridRef) =>
        hasAiQueryGenerationAccess && (
            <CustomIconButton
                title={getLocalizedString("generateOrUpdateWithAI")}
                onClick={() => handleOpenCreateAIQueryDialog(gridRef)}
                type={"addAi"}
            />
        );

    const deleteItem = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("delete")}
            onClick={() => handleOpenDeleteDialog(gridRef)}
            type={"delete"}
        />
    );

    const editItem = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("edit")}
            onClick={() => handleOpenEditDialog(gridRef)}
            type={"edit"}
        />
    );

    const duplicateItem = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("duplicate")}
            onClick={() => handleOpenDuplicateDialog(gridRef)}
            type={"duplicate"}
        />
    );

    const executeItem = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("executeQuery")}
            onClick={() => handleExecute(gridRef)}
            type={"execute"}
        />
    );

    const exportItem = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("export")}
            onClick={() => handleExport(gridRef)}
            type={"download"}
        />
    );

    const importItem = () => (
        <CustomIconButton title={getLocalizedString("import")} onClick={() => handleImport()} type={"upload"} />
    );

    const handleRowClick = (row) => {
        handleOpenParameterPage(row.data);
    };

    const helpItems = [
        <MenuItemWithIcon
            key={"b"}
            value={"/resources/ICARIUS - Definición de gestión (Consultas-GeoAnálisis-KPI).pdf"}
            text={getLocalizedString("managementDefinitionConsultationsGeoAnalysisKPI")}
            type={"file"}
        />,
    ];

    const searchItem = (ref) => <SearchConcept gridRef={ref} data={data} />;

    return (
        <>
            {createEditDialogIsOpen ? (
                <FormContainer isLoading={isLoading} data={selectedItem} handleClose={handleCloseEditDialog} />
            ) : (
                <CommonPage
                    columnDefPage={paths.queryDefinition}
                    title={getLocalizedString("queryDefinition")}
                    gridTitle={getLocalizedString("queryDefinition")}
                    menuItems={[
                        goBackItem,
                        searchItem,
                        addItem,
                        addWithAiItem,
                        editItem,
                        deleteItem,
                        duplicateItem,
                        executeItem,
                        exportItem,
                        importItem,
                    ]}
                    helpItems={helpItems}
                    rowData={data}
                    dateFormat={dateFormat}
                    isLoading={isExportinGrid || isLoading}
                    handleRowClick={handleRowClick}
                    hasExpand
                    hasHelp
                >
                    {deleteDialogIsOpen && !isLoading && (
                        <ConfirmationDialog
                            open={deleteDialogIsOpen}
                            title={getLocalizedString("atention")}
                            text={getLocalizedString("areYouSureYouWantToDeleteQueryDefinition")}
                            handleConfirm={handleDelete}
                            handleClose={handleCloseDeleteDialog}
                        />
                    )}
                    {duplicateDialogIsOpen && !isLoading && (
                        <DuplicateDialog
                            open={duplicateDialogIsOpen}
                            data={selectedItem}
                            handleConfirm={handleDuplicate}
                            handleClose={handleCloseDuplicateDialog}
                        />
                    )}
                    {aiAssistedCreateDialogIsOpen && (
                        <CreateOrUpdateAiAssistedDialog
                            open={aiAssistedCreateDialogIsOpen}
                            data={selectedItem}
                            handleClose={handleCloseAiAssistedCreateDialog}
                        />
                    )}
                </CommonPage>
            )}
        </>
    );
};

export default QueryDefinition;
